/* =========================================================================
   Collections screen (§4). Three active sets rendered as a merged view — the
   server catalog (getCollections; includes the account's earned state) MAX-merged
   with local earnings ('catholics.collectibles' + 'catholics.feasts'), so guests
   and offline players see a full, honest collection and accounts see their real
   standing. Items render EARNED (gold + icon + date) vs LOCKED (dimmed
   silhouette).

   • Feast Days → a month-grouped CALENDAR of the badges earned from the daily
     feast moment (placed by the feast's calendar date).
   • Relics → redeemable: while the player holds a relic_token (qty>0), each
     un-earned relic shows a "Redeem ⚱️" affordance (spend a token → earn locally
     + CollectibleEarned + authed grant + Theo pop).
   • Apostles → earned as Pilgrimage 1 step rewards (no redemption).
   ========================================================================= */
import { esc } from './engine';
import { icon } from './ui/icons';
import { byId, show } from './dom';
import { track } from './telemetry';
import { getLine } from './characters/registry';
import { triggerCharPop } from './characters/ui';
import { isAuthenticated, getCollections, type CollectionSet, type CollectionItem } from './api';
import { mergeLive, earnCollectible, hasCollectible } from './collectibles';
import { readFeasts } from './feasts';
import { getQty, spendItem } from './inventory';
import { RELIC_KEYS } from './data/collectionsData';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

let sets: CollectionSet[] = [];

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

/* ------------------------------ feast calendar ------------------------------ */
function renderFeastCalendar(set: CollectionSet): string {
  // Drive from local feast badges (which carry the feast's calendar date); merge
  // in any server-earned feast items we can't date under an "on your account" note.
  const feasts = readFeasts();
  const byMonth = new Map<number, { title: string; date: string }[]>();
  let earnedCount = 0;
  for (const b of Object.values(feasts)) {
    const m = Number((b.date || '').slice(5, 7)) - 1; // 0-based month from YYYY-MM-DD
    if (m < 0 || m > 11) continue;
    if (!byMonth.has(m)) byMonth.set(m, []);
    byMonth.get(m)!.push({ title: b.title, date: b.date });
    earnedCount++;
  }
  const serverEarned = set.items.filter((i) => i.earnedUtc).length;
  const undated = Math.max(0, serverEarned - earnedCount);

  if (earnedCount === 0 && undated === 0) {
    return (
      `<div class="coll-section"><div class="coll-set-head">${icon('sparkle', 18)} ${esc(set.title)} <span class="coll-count">0</span></div>` +
      `<div class="coll-empty">Visit today's feast moment to earn your first badge, one for every day the Church keeps a feast.</div></div>`
    );
  }

  let months = '';
  for (let m = 0; m < 12; m++) {
    const items = byMonth.get(m);
    if (!items || items.length === 0) continue;
    const badges = items
      .map(
        (it) =>
          `<div class="cal-badge earned" title="${esc(it.title)} · ${esc(it.date)}">${icon('sparkle', 16)}<span class="cal-badge-day">${esc(it.date.slice(8, 10))}</span></div>`,
      )
      .join('');
    months += `<div class="cal-month"><div class="cal-month-name">${MONTHS[m]}</div><div class="cal-badges">${badges}</div></div>`;
  }
  const note = undated > 0 ? `<div class="coll-note-inline">+${undated} more earned on your account.</div>` : '';
  return (
    `<div class="coll-section"><div class="coll-set-head">${icon('sparkle', 18)} ${esc(set.title)} <span class="coll-count">${earnedCount + undated}</span></div>` +
    `<div class="cal-grid">${months}</div>${note}</div>`
  );
}

/* ------------------------------- item grids ------------------------------- */
function itemTile(item: CollectionItem, redeemable: boolean): string {
  const earned = !!item.earnedUtc;
  const ic = item.icon ? esc(item.icon) : icon('star', 22);
  if (earned) {
    return (
      `<div class="coll-item earned">` +
      `<span class="ci-ic">${ic}</span>` +
      `<span class="ci-title">${esc(item.title)}</span>` +
      `<span class="ci-date">${esc(fmtDate(item.earnedUtc))}</span>` +
      `</div>`
    );
  }
  return (
    `<div class="coll-item locked">` +
    `<span class="ci-ic silhouette">${ic}</span>` +
    `<span class="ci-title">${esc(item.title)}</span>` +
    (redeemable
      ? `<button class="btn secondary ci-redeem" type="button" data-key="${esc(item.key)}">Redeem ${icon('urn', 14)}</button>`
      : `<span class="ci-locked-lbl">Locked</span>`) +
    `</div>`
  );
}

function renderGridSet(set: CollectionSet): string {
  const earnedCount = set.items.filter((i) => i.earnedUtc).length;
  const tokens = getQty('relic_token');
  const isRelics = set.key === 'relics';
  const tokenLine =
    isRelics && tokens > 0
      ? `<div class="coll-token-line">${icon('urn', 16)} You hold <b>${tokens}</b> relic token${tokens === 1 ? '' : 's'}. Redeem below.</div>`
      : isRelics
        ? `<div class="coll-token-line dim">Find relic tokens on the bonus wheel to redeem these.</div>`
        : '';
  const tiles = set.items
    .map((it) => itemTile(it, isRelics && !it.earnedUtc && tokens > 0))
    .join('');
  return (
    `<div class="coll-section">` +
    `<div class="coll-set-head">${set.icon ? esc(set.icon) : icon('star', 18)} ${esc(set.title)} <span class="coll-count">${earnedCount}/${set.items.length}</span></div>` +
    tokenLine +
    `<div class="coll-grid">${tiles}</div></div>`
  );
}

/* -------------------------------- redeem -------------------------------- */
function redeemRelic(itemKey: string): void {
  if (!RELIC_KEYS.includes(itemKey)) return;
  if (hasCollectible(itemKey)) return; // double-click guard: never burn a token on an owned relic
  if (getQty('relic_token') <= 0) return;
  if (!spendItem('relic_token', 1, 'relic_redeem')) return;
  earnCollectible(itemKey); // local earn + CollectibleEarned + authed grant
  try {
    triggerCharPop(getLine('theo', 'gallery-generic'));
  } catch {
    /* no DOM */
  }
  renderAll(); // reflect the new balance + earned relic
}

/* -------------------------------- render -------------------------------- */
function renderAll(): void {
  const body = document.getElementById('collectionsBody');
  if (!body) return;
  const note = byId('collectionsNote');
  if (!isAuthenticated()) {
    note.classList.remove('hidden');
    note.textContent = 'Playing as guest. Your collection lives on this device. Create a free account to keep it safe and synced.';
  } else {
    note.classList.add('hidden');
  }
  body.innerHTML = sets
    .map((s) => (s.key === 'feast_days' ? renderFeastCalendar(s) : renderGridSet(s)))
    .join('');
  body.querySelectorAll<HTMLButtonElement>('.ci-redeem').forEach((b) => {
    b.onclick = () => redeemRelic(b.dataset.key || '');
  });
}

/* -------------------------------- public -------------------------------- */
export async function openCollections(): Promise<void> {
  track('AreaOpened', { area: 'collections' });
  byId('bankbar').classList.add('hidden');
  show('collections');
  sets = mergeLive(null); // instant local render
  renderAll();
  const r = await getCollections(); // then upgrade with the server catalog when reachable
  sets = mergeLive(r.ok ? r.data : null);
  renderAll();
}

export function initCollections(): void {
  byId('collectionsBackBtn').addEventListener('click', () => {
    byId('bankbar').classList.add('hidden');
    show('home');
  });
}
